import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  fetchAttractionRulesForPackage,
  fetchPackageAttractionLines,
} from "../services/attractionsApi";
import { buildAttractionRulesRequestForPackageProfile } from "../utils/attractions/buildAttractionRulesRequestForPackageProfile";
import { fetchCatalogForPackageProfile } from "../utils/attractions/fetchCatalogForPackageProfile";
import {
  buildRuleByAttractionMap,
  dedupeRulesForPackageProfile,
  dynamicPackageAttractionLineBillableToCustomer,
  getBillableAttractionsTotalGross,
  partitionRulesForPackageProfile,
} from "../utils/packageAttractionRulesDisplay";

const PackageProfileAttractionsContext = createContext(null);

/**
 * טעינה משותפת של חוקי אטרקציה + שורות חבילה לפרופיל חבילה (packages/profile).
 * מאפשר סנכרון סכום חייב (billable) עם סיכום מחיר ורענון אחרי הוספה.
 */
export function PackageProfileAttractionsProvider({
  children,
  dynamicPackageId = null,
  destinationId = null,
  packageKey = null,
  flightScopeValue = null,
  visitDate = null,
  partySize = 1,
  onBillableTotalChange = null,
}) {
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [linesPayload, setLinesPayload] = useState(null);
  const [rulesError, setRulesError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [catalogByAid, setCatalogByAid] = useState(() => new Map());

  const billableCallbackRef = useRef(onBillableTotalChange);
  billableCallbackRef.current = onBillableTotalChange;

  const refresh = useCallback(() => {
    setRefreshToken((t) => t + 1);
  }, []);

  const loadPlan = useMemo(
    () =>
      buildAttractionRulesRequestForPackageProfile({
        packageId: dynamicPackageId,
        destinationId,
        packageKey,
        flightScopeValue,
        visitDate,
        limit: 80,
      }),
    [dynamicPackageId, destinationId, packageKey, flightScopeValue, visitDate]
  );

  const pkgRef =
    loadPlan.mode === "package" && loadPlan.fetchLinesForPackageId != null
      ? loadPlan.fetchLinesForPackageId
      : (() => {
          if (dynamicPackageId == null || dynamicPackageId === "") return null;
          const st = String(dynamicPackageId).trim();
          if (!st) return null;
          if (
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
              st
            )
          ) {
            return st;
          }
          const n = Number(st);
          return Number.isFinite(n) && n > 0 ? n : st;
        })();

  useEffect(() => {
    let cancelled = false;
    if (loadPlan.mode === "none") {
      setRulesLoading(false);
      setRules([]);
      setLinesPayload(null);
      setRulesError(null);
      setCatalogByAid(new Map());
      billableCallbackRef.current?.(0);
      return undefined;
    }
    setRulesLoading(true);
    setRulesError(null);
    (async () => {
      try {
        const rulesRes = await fetchAttractionRulesForPackage(loadPlan.requestParams);
        const linesPromise =
          loadPlan.fetchLinesForPackageId != null
            ? fetchPackageAttractionLines(loadPlan.fetchLinesForPackageId).catch(() => ({
                lines: [],
                meta: {},
              }))
            : Promise.resolve({ lines: [], meta: {} });
        const linesRes = await linesPromise;
        if (cancelled) return;
        let rulesArr = Array.isArray(rulesRes?.rules) ? rulesRes.rules : [];
        const req = loadPlan.requestParams || {};
        if (
          req.dynamic_package_id != null &&
          req.flight_scope_value != null &&
          String(req.flight_scope_value).trim() !== ""
        ) {
          try {
            const scopeRes = await fetchAttractionRulesForPackage({
              limit: req.limit ?? 80,
              flight_scope_value: req.flight_scope_value,
              scope_value: req.flight_scope_value,
            });
            const scopeRules = Array.isArray(scopeRes?.rules) ? scopeRes.rules : [];
            if (scopeRules.length) {
              rulesArr = dedupeRulesForPackageProfile([...rulesArr, ...scopeRules]);
            }
          } catch {
            /* secondary scope-only rules optional */
          }
        }
        setRules(rulesArr);
        setLinesPayload(linesRes && typeof linesRes === "object" ? linesRes : null);
      } catch (e) {
        if (!cancelled) {
          setRules([]);
          setLinesPayload(null);
          setRulesError(
            e?.response?.data?.message || e?.message || "טעינת אטרקציות נכשלה"
          );
        }
      } finally {
        if (!cancelled) setRulesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPlan, refreshToken]);

  useEffect(() => {
    let cancelled = false;
    if (loadPlan.mode === "none") {
      setCatalogByAid(new Map());
      return undefined;
    }
    (async () => {
      const m = await fetchCatalogForPackageProfile({
        flightScopeValue,
        destinationId,
      });
      if (!cancelled) setCatalogByAid(m);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPlan.mode, flightScopeValue, destinationId]);

  const dedupedRules = useMemo(() => dedupeRulesForPackageProfile(rules), [rules]);
  /** ל-view models ול־UI — חוקים אחרי dedupe + מיזוג קטלוג (תמונות/מחירים שלא ב־GET /rules slim). */
  const rulesForView = useMemo(() => {
    let mergedWithCatalog = 0;
    const missingCatalogAid = [];
    const out = dedupedRules.map((r) => {
      const aid = r?.attraction_id != null ? Number(r.attraction_id) : NaN;
      if (!Number.isFinite(aid) || !catalogByAid.has(aid)) {
        if (Number.isFinite(aid)) missingCatalogAid.push(aid);
        return r;
      }
      mergedWithCatalog += 1;
      const fromCatalog = catalogByAid.get(aid);
      const prevCat =
        r.catalog && typeof r.catalog === "object" ? r.catalog : {};
      return {
        ...r,
        catalog: { ...prevCat, ...(fromCatalog && typeof fromCatalog === "object" ? fromCatalog : {}) },
      };
    });
    return out;
  }, [dedupedRules, catalogByAid]);
  const { included, gifts } = useMemo(
    () => partitionRulesForPackageProfile(rulesForView),
    [rulesForView]
  );
  const ruleByAttractionId = useMemo(
    () => buildRuleByAttractionMap(rulesForView),
    [rulesForView]
  );

  const billableTotalGross = useMemo(
    () => getBillableAttractionsTotalGross(linesPayload || {}),
    [linesPayload]
  );

  const lines = useMemo(
    () =>
      Array.isArray(linesPayload?.lines) ? linesPayload.lines : [],
    [linesPayload]
  );

  const attachedAttractionIds = useMemo(() => {
    const ids = new Set();
    for (const row of lines) {
      const aid = row?.attraction_id;
      if (aid != null && !Number.isNaN(Number(aid))) ids.add(Number(aid));
    }
    return ids;
  }, [lines]);

  const billableLines = useMemo(
    () => lines.filter(dynamicPackageAttractionLineBillableToCustomer),
    [lines]
  );

  useEffect(() => {
    if (loadPlan.mode === "none") {
      billableCallbackRef.current?.(0);
      return;
    }
    if (loadPlan.mode === "destination") {
      billableCallbackRef.current?.(0);
      return;
    }
    if (pkgRef == null) return;
    billableCallbackRef.current?.(billableTotalGross);
  }, [loadPlan.mode, pkgRef, billableTotalGross]);

  const value = useMemo(
    () => ({
      packageId: pkgRef,
      attractionsContextMode: loadPlan.mode,
      rulesLoading,
      rulesError,
      refresh,
      included,
      gifts,
      rulesForView,
      ruleByAttractionId,
      billableTotalGross,
      lines,
      billableLines,
      linesPayload,
      attachedAttractionIds,
      partySize: Number(partySize) > 0 ? Number(partySize) : 1,
    }),
    [
      pkgRef,
      loadPlan.mode,
      rulesLoading,
      rulesError,
      refresh,
      included,
      gifts,
      rulesForView,
      ruleByAttractionId,
      billableTotalGross,
      lines,
      billableLines,
      linesPayload,
      attachedAttractionIds,
      partySize,
    ]
  );

  return (
    <PackageProfileAttractionsContext.Provider value={value}>
      {children}
    </PackageProfileAttractionsContext.Provider>
  );
}

export function usePackageProfileAttractions() {
  const ctx = useContext(PackageProfileAttractionsContext);
  if (!ctx) {
    throw new Error(
      "usePackageProfileAttractions must be used within PackageProfileAttractionsProvider"
    );
  }
  return ctx;
}

export function usePackageProfileAttractionsOptional() {
  return useContext(PackageProfileAttractionsContext);
}
